import BreadCrumb from "Components/Common/BreadCrumb";
import React, { useState, useMemo, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, Badge, Button as RButton, Spinner } from "reactstrap";
import Heading from "Components/Common/Heading";
import Filter from "Components/Common/Filter";
import Table, { Column } from "Components/Common/Table";
import Widgets from "Components/Common/Widgets";
import AddCondominiumModal from "./AddCondominiumModal";
import { getAllCondos } from "endpoints/condo/index.action";
import { useTranslation } from "react-i18next";
import "./Condominiums.scss";

interface Condominium {
  id: string;
  name: string;
  condoNumber?: string;
  streetAddress?: string;
  address?: string;
  numberOfUnits?: number;
  totalUnits?: number;
  status: "active" | "inactive";
  dateCreated: string;
  lastUpdated: string;
  createdAt?: string;
  updatedAt?: string;
}

const Condominiums = () => {
  const { t } = useTranslation();
  document.title = t("condominiums.title");

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [condominiums, setCondominiums] = useState<Condominium[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch condos from API
  const fetchCondos = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllCondos({ page: 1, limit: 100 });
      
      if (response?.status === 'success' && response?.data) {
        const condosData = response.data.condos || response.data || [];
        
        // Map backend data to frontend format
        const mappedCondos: Condominium[] = condosData.map((condo: any) => ({
          id: condo.id,
          name: condo.name,
          condoNumber: condo.condoNumber || `C-${condo.id.slice(-3)}`,
          streetAddress: condo.address || `${condo.address || ''}${condo.city ? `, ${condo.city}` : ''}`.trim(),
          address: condo.address,
          numberOfUnits: condo.totalUnits || condo.numberOfUnits || 0,
          totalUnits: condo.totalUnits,
          status: "active", // Default to active, you can add status field to backend if needed
          dateCreated: condo.createdAt 
            ? new Date(condo.createdAt).toLocaleString("en-US", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "N/A",
          lastUpdated: condo.updatedAt
            ? new Date(condo.updatedAt).toLocaleString("en-US", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "N/A",
        }));
        
        setCondominiums(mappedCondos);
      } else {
        setError(response?.message || t("condominiums.failedToLoad"));
      }
    } catch (err: any) {
      console.error("Error fetching condos:", err);
      setError(err?.message || t("condominiums.failedToLoadTryAgain"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCondos();
  }, []);

  // Calculate summary statistics
  const summaryWidgets = useMemo(() => {
    const total = condominiums.length;
    const active = condominiums.filter((c: Condominium) => c.status === "active").length;
    const inactive = condominiums.filter((c: Condominium) => c.status === "inactive").length;

    return [
      {
        id: 1,
        label: t("condominiums.totalCondominiums"),
        counter: total.toString(),
        bgColor: "#E3F2FD", // Light blue
      },
      {
        id: 2,
        label: t("condominiums.activeCondominiums"),
        counter: active.toString(),
        bgColor: "#E8F5E9", // Light green
      },
      {
        id: 3,
        label: t("condominiums.inactiveCondominiums"),
        counter: inactive.toString(),
        bgColor: "#FCE4EC", // Light pink
      },
    ];
  }, [condominiums, t]);

  // Filter condominiums based on search
  const filteredCondominiums = useMemo(() => {
    if (!searchQuery) return condominiums;

    const query = searchQuery.toLowerCase();
    return condominiums.filter(
      (condo: Condominium) =>
        condo.name.toLowerCase().includes(query) ||
        (condo.condoNumber && condo.condoNumber.toLowerCase().includes(query)) ||
        (condo.streetAddress && condo.streetAddress.toLowerCase().includes(query)) ||
        condo.id.toLowerCase().includes(query)
    );
  }, [condominiums, searchQuery]);

  const toggleAddModal = () => {
    setIsAddModalOpen(!isAddModalOpen);
  };

  const columns: Column<Condominium>[] = [
    {
      key: "id",
      title: "ID",
      width: 100,
    },
    {
      key: "name",
      title: t("condominiums.condominiumName"),
      width: 200,
    },
    {
      key: "condoNumber",
      title: t("condominiums.condoNumberShort"),
      width: 100,
    },
    {
      key: "streetAddress",
      title: t("condominiums.streetAddress"),
      width: 250,
    },
    {
      key: "numberOfUnits",
      title: t("condominiums.numberOfUnitsShort"),
      width: 120,
      align: "center",
    },
    {
      key: "status",
      title: t("common.status"),
      width: 120,
      align: "center",
      render: (value) => (
        <Badge
          color={value === "active" ? "success" : "danger"}
          className="condominium-status-badge"
        >
          {value === "active" ? t("common.active") : t("common.inactive")}
        </Badge>
      ),
    },
    {
      key: "dateCreated",
      title: t("condominiums.dateCreated"),
      width: 180,
    },
    {
      key: "lastUpdated",
      title: t("condominiums.lastUpdated"),
      width: 180,
    },
    {
      key: "actions",
      title: "",
      width: 50,
      align: "center",
      render: () => (
        <RButton
          color="link"
          className="condominium-action-btn"
        >
          <i className="ri-more-2-fill"></i>
        </RButton>
      ),
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("condominiums.title")}
            pageTitles={[
              { label: t("condominiums.home"), path: "/dashboard" },
              { label: t("condominiums.condominium") },
            ]}
          />

          {/* Summary Cards */}
          <Row className="condominiums-widgets-row mb-4">
            <Widgets data={summaryWidgets} />
          </Row>

          {/* Map View Placeholder */}
          <Row className="mb-4">
            <Col xs={12}>
              <Card className="condominium-map-card">
                <CardBody className="condominium-map-body">
                  <div className="condominium-map-placeholder">
                    <i className="ri-map-pin-line condominium-map-icon"></i>
                    <p className="condominium-map-title">{t("condominiums.mapViewTitle")}</p>
                    <p className="condominium-map-subtitle">{t("condominiums.mapIntegrationPlaceholder")}</p>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Table Section */}
          <Row className="align-items-center justify-content-between mb-4">
            <Col xs={12} lg={8} className="mb-3 mb-lg-0">
              <Heading
                title={t("condominiums.manageCondominium")}
                description={t("condominiums.addCondominiumDescription")}
                button={false}
              />
            </Col>
            <Col xs={12} lg={4}>
              <div className="d-flex flex-column flex-sm-row gap-3 align-items-stretch align-items-sm-center">
                <div className="flex-grow-1">
                  <Filter
                    placeholder={t("condominiums.searchCondominiums")}
                    value={searchQuery}
                    onSearch={setSearchQuery}
                    onChange={setSearchQuery}
                  />
                </div>
                <RButton
                  color="primary"
                  onClick={toggleAddModal}
                  className="create-condominium-btn"
                >
                  <i className="ri-add-circle-line me-2"></i>
                  {t("condominiums.addCondominium")}
                </RButton>
              </div>
            </Col>
          </Row>

          {/* Table */}
          <Row>
            <Col xs={12}>
              {loading ? (
                <Card className="text-center p-5">
                  <CardBody>
                    <Spinner color="primary" />
                    <p className="text-muted mt-3">{t("condominiums.loadingCondominiums")}</p>
                  </CardBody>
                </Card>
              ) : error ? (
                <Card className="text-center p-5">
                  <CardBody>
                    <i className="ri-error-warning-line text-danger" style={{ fontSize: "3rem" }}></i>
                    <p className="text-danger mt-3 mb-0">{error}</p>
                    <RButton color="primary" className="mt-3" onClick={() => window.location.reload()}>
                      {t("condominiums.retry")}
                    </RButton>
                  </CardBody>
                </Card>
              ) : filteredCondominiums.length === 0 ? (
                <Card className="text-center p-5">
                  <CardBody>
                    <i className="ri-building-line text-muted" style={{ fontSize: "3rem" }}></i>
                    <p className="text-muted mt-3 mb-0">
                      {searchQuery ? t("condominiums.noCondominiumsFound") : t("condominiums.noCondominiumsCreateFirst")}
                    </p>
                  </CardBody>
                </Card>
              ) : (
                <div className="condominiums-table-wrapper">
                  <Table
                    columns={columns}
                    data={filteredCondominiums}
                    pagination={{
                      pageIndex: 0,
                      pageSize: 10,
                      total: filteredCondominiums.length,
                    }}
                    selectable
                    onSelectionChange={(selected) =>
                      console.log("Selected:", selected)
                    }
                  />
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      <AddCondominiumModal 
        isOpen={isAddModalOpen} 
        toggle={toggleAddModal}
        onCondoCreated={fetchCondos}
      />
    </React.Fragment>
  );
};

export default Condominiums;

